import type {
	CAFilterCategory,
	CAFilterOption,
	CAFilterValue,
	CASelectedFilters,
	CASelectInputType,
} from '@ds/interfaces'
import { DURATION, PER_PAGE } from '@vuejs/helpers/_constants'
import api from '@vuejs/services/api'
import {
	getComplianceBundles,
	getFullCatalog,
	getSpecializedBundles,
} from '@vuejs/services/catalog'
import type {
	CatalogList,
	CatalogPagination,
	CatalogPaginationTotal,
} from '@vuejs/services/catalog/interfaces'
import type {
	Certification,
	ComplianceBundle,
	CourseType,
} from '@vuejs/services/courses/interfaces'
import { useFeatureFlagStore } from '@vuejs/stores/FeatureFlagStore'
import GlobalStore from '@vuejs/stores/GlobalStore'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getAgencyId } from '../utils/getAgencyId'

const catalogFormat: CAFilterOption = {
	categoryLabel: 'Format',
	dataTest: 'ca-filter-option-catalog-format',
	filterValues: [{ id: 'video', label: 'Video only' }],
	id: 'format',
	useRadioButtons: true,
}

const catalogDefaultOptions: CAFilterOption[] = [
	{
		categoryLabel: 'Plan',
		categoryTooltip: 'Upgrade to assign training outside of your plan',
		dataTest: 'ca-filter-option-catalog-plan',
		filterValues: [
			{ id: 'in plan', label: 'In-plan' },
			{ id: 'out of plan', label: 'Out-of-plan' },
		],
		id: 'plan',
	},
	{
		categoryLabel: 'Duration',
		dataTest: 'ca-filter-option-catalog-duration',
		filterValues: [
			{ id: DURATION.SHORT, label: 'Up to 15 min' },
			{ id: DURATION.MEDIUM, label: 'Up to 30 min' },
			{ id: DURATION.LONG, label: 'Up to 45 min' },
			{ id: DURATION.LONGER, label: 'Up to 1 hr' },
			{ id: DURATION.FILTER, label: 'Over 1 hr' },
		],
		id: 'duration',
		useRadioButtons: true,
	},
	{
		categoryLabel: 'Language(s)',
		dataTest: 'ca-filter-option-catalog-language',
		filterValues: [{ id: 'spanish', label: 'Spanish full translation' }],
		id: 'language',
	},
	{
		categoryLabel: 'Audience',
		dataTest: 'ca-filter-option-catalog-medical',
		filterValues: [
			{ id: 'non-medical', label: 'Non-medical' },
			{ id: 'medical', label: 'Medical' },
		],
		id: 'audience',
	},
]

export const useCatalogStore = defineStore('catalog', () => {
	// State
	const careSettings = ref<CAFilterOption>({
		categoryLabel: 'Care setting',
		dataTest: 'ca-filter-option-care-setting',
		filterValues: [
			{ id: 'home care', label: 'Home care' },
			{ id: 'assisted living', label: 'Assisted living' },
			{ id: 'hospice care', label: 'Hospice care' },
			{ id: 'home health', label: 'Home health' },
			{ id: 'adult day', label: 'Adult day' },
			{ id: 'skilled nursing', label: 'Skilled nursing' },
		],
		id: 'care-setting',
	})
	const catalogList = ref<CatalogList[]>([])
	const catalogRouteName = ref('FullCatalog')
	const complianceBundles = ref<ComplianceBundle[]>([])
	const itemType = ref<CourseType>('course')
	const loading = ref(false)
	const pagination = ref<CatalogPagination>({
		currentPage: 1,
		currentSearchPhrase: '',
		filterValues: {},
		perPage: PER_PAGE.COURSES,
	})
	const paginationTotal = ref<CatalogPaginationTotal>({
		complianceBundles: 0,
		fullCatalog: 0,
		specializedBundles: 0,
	})
	const payorSource = ref<CAFilterOption>({
		categoryLabel: 'Payor source',
		categoryTooltip: 'Payor source indicates who pays for care. Public pay includes Medicaid, Medicare, VA benefits, or other state-funded programs.',
		dataTest: 'ca-filter-option-payor-source',
		filterValues: [
			{ id: 'private', label: 'Private pay' },
			{ id: 'public', label: 'Public pay' },
		],
		id: 'payor-source',
	})
	const roles = ref<CAFilterOption>({
		categoryLabel: 'Role',
		dataTest: 'ca-filter-option-role',
		filterValues: [
			{ id: 'manager', label: 'Manager' },
			{ id: 'non-medical caregiver', label: 'Non-medical caregiver' },
			{ id: 'certified nursing assistant', label: 'Certified nursing assistant' },
			{
				children: [
					{ id: 'nurse', label: 'Nurse' },
					{ id: 'physical therapist', label: 'Physical therapist' },
					{ id: 'occupational therapist', label: 'Occupational therapist' },
				],
				id: 'clinician',
				label: 'Clinician',
			},
			{
				children: [
					{ id: 'administrators', label: 'Administrator' },
					{ id: 'dining staff', label: 'Dining staff' },
					{ id: 'housing_staff', label: 'Housekeeping staff' },
					{ id: 'maintenance staff', label: 'Maintenance staff' },
					{ id: 'office staff', label: 'Office staff' },
					{ id: 'other staff', label: 'Other staff' },
				],
				id: 'staff-parent',
				label: 'Staff',
			},
		],
		id: 'role',
	})
	const selectedFilterOptions = ref<CASelectedFilters>({})
	const specializedBundles = ref<Certification[]>([])

	// Getters
	const filterOptions = computed(() => {
		const featureFlagStore = useFeatureFlagStore()

		const filterOptionsBasedOnFeatureFlag = featureFlagStore.isFeatureEnabled('curriculum_discoverability')
			? [
				...catalogDefaultOptions.slice(0, 2),
				catalogFormat,
				...catalogDefaultOptions.slice(2),
			]
			: catalogDefaultOptions

		return [
			...filterOptionsBasedOnFeatureFlag,
			careSettings.value,
			payorSource.value,
			roles.value,
		]
	})

	const filterParameters = computed(() => {
		return {
			agencyId: getAgencyId(),
			filterValues: { ...pagination.value.filterValues },
			page: pagination.value.currentPage,
			perPage: pagination.value.perPage,
			search: pagination.value.currentSearchPhrase,
		}
	})

	const selectedFilterCategories = computed<CAFilterCategory[]>(() => {
		const complianceBundlesOnlyCategories = [
			'Care setting',
			'Payor source',
			'Role',
		]

		if (!!selectedFilterOptions.value) {
			const categories = Object.entries(selectedFilterOptions.value).map(([
				id,
				filterIds,
			]) => {
				const filterOption = filterOptions.value.find(o => o.id === id)
				const filterArray = Array.isArray(filterIds) ? filterIds : [filterIds]
				const filterLabels = filterArray.map(id => {
					const match = filterOption?.filterValues.find(v => {
						if (`${v.id}` === `${id}`) {
							return true
						}

						return v.children?.some(child => `${child.id}` === `${id}`)
					})

					const matchedItem = match && `${match.id}` === `${id}`
						? match
						: match?.children?.find(child => `${child.id}` === `${id}`)

					return matchedItem ?
						{ id: String(matchedItem.id), label: matchedItem.label } :
						{ id: String(id), label: String(id) }
				})

				return {
					categoryId: id,
					categoryLabel: filterOption?.categoryLabel || id,
					filterLabels,
				}
			})

			return catalogRouteName.value === 'ComplianceBundles' ?
				categories :
				categories.filter(filter => !complianceBundlesOnlyCategories.includes(filter.categoryLabel))
		}

		return []
	})

	const showFilters = computed(() => !!selectedFilterOptions.value)

	// Actions
	async function fetchCatalogs() {
		switch (catalogRouteName.value) {
			case 'ComplianceBundles':
				await fetchComplianceBundles()
				break

			case 'SpecializedBundles':
				await fetchSpecializedBundles()
				break

			case 'FullCatalog':
			default:
				await fetchFullCatalog()
				break
		}
	}

	async function fetchComplianceBundles() {
		loading.value = true

		try {
			const response = await getComplianceBundles(api, filterParameters.value)

			complianceBundles.value = response.data
			paginationTotal.value.complianceBundles = response.data.length
			itemType.value = 'compliance'
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		} finally {
			loading.value = false
		}
	}

	async function fetchFullCatalog() {
		loading.value = true

		try {
			const response = await getFullCatalog(api, filterParameters.value)

			catalogList.value = response.data
			paginationTotal.value.fullCatalog = response.pagination.total
			itemType.value = 'course'
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		} finally {
			loading.value = false
		}
	}

	async function fetchSpecializedBundles() {
		loading.value = true

		try {
			const response = await getSpecializedBundles(api, filterParameters.value)

			specializedBundles.value = response.data
			paginationTotal.value.specializedBundles = response.pagination.total
			itemType.value = 'specialized'
		} catch (error) {
			GlobalStore.onHandleErrorMessages({ error })
		} finally {
			loading.value = false
		}
	}

	function findFilterValueById(filterValues: CAFilterValue[], filterId: CASelectInputType): CAFilterValue | undefined {
		const directMatch = filterValues.find(value => value.id === filterId)

		if (directMatch) {
			return directMatch
		}

		for (const value of filterValues) {
			if (value.children) {
				const childMatch = value.children.find(child => child.id === filterId)

				if (childMatch) {
					return childMatch
				}
			}
		}

		return undefined
	}

	async function handleFilter(filters: CASelectedFilters) {
		const currentFilters = pagination.value.filterValues

		for (const [
			key,
			value,
		] of Object.entries(filters)) {
			if (value === undefined || Array.isArray(value) && value.length === 0) {
				delete currentFilters[key]
			} else {
				switch (key) {
					case 'duration':
						const durationFilter = filterOptions.value.find(option => option.id === 'duration')

						const durationOption = durationFilter?.filterValues.find(
							filterValue => `${filterValue.id}` === `${Array.isArray(value) ? value[0] : value}`
						)

						currentFilters['duration'] = typeof durationOption?.id === 'number' ? durationOption.id : DURATION.FILTER
						break

					case 'format':
						const formatFilter = filterOptions.value.find(option => option.id === 'format')

						const formatOption = formatFilter?.filterValues.find(
							filterValue => `${filterValue.id}` === `${Array.isArray(value) ? value[0] : value}`
						)

						if (formatOption) {
							currentFilters['format'] = [formatOption.id]
						}
						break

					default:
						currentFilters[key] = value

						break
				}
			}
		}

		selectedFilterOptions.value = currentFilters

		await handlePageChange(1)
	}

	async function handlePageChange(page: number) {
		pagination.value.currentPage = page
		await fetchCatalogs()
	}

	async function handleRemoveFilter(categoryId: string, filterId: string) {
		removeFilterOption(categoryId, filterId)

		pagination.value.currentPage = 1
		await fetchCatalogs()
	}

	function handleSearchChange(search: string) {
		pagination.value.currentPage = 1
		pagination.value.currentSearchPhrase = search
	}

	function removeFilterOption(categoryId: string, filterId: string) {
		switch (categoryId) {
			case 'duration':
				delete pagination.value.filterValues.duration
				delete selectedFilterOptions.value[categoryId]
				break

			case 'format':
				delete pagination.value.filterValues.format
				delete selectedFilterOptions.value[categoryId]
				break

			default: {
				const currentValues = selectedFilterOptions.value[categoryId]

				if (Array.isArray(currentValues)) {
					const updatedValues = currentValues.filter(id => id !== filterId)

					if (updatedValues.length > 0) {
						selectedFilterOptions.value[categoryId] = updatedValues
					} else {
						delete selectedFilterOptions.value[categoryId]
					}
				} else {
					delete selectedFilterOptions.value[categoryId]
				}

				if (pagination.value.filterValues[categoryId]) {
					const paginationValues = pagination.value.filterValues[categoryId]

					if (Array.isArray(paginationValues)) {
						const updatedPaginationValues = paginationValues.filter(id => id !== filterId)

						if (updatedPaginationValues.length > 0) {
							pagination.value.filterValues[categoryId] = updatedPaginationValues
						} else {
							delete pagination.value.filterValues[categoryId]
						}
					} else {
						delete pagination.value.filterValues[categoryId]
					}
				}

				break
			}
		}
	}

	async function resetAllFilters() {
		for (const [
			categoryLabel,
			filterIds,
		] of Object.entries(selectedFilterOptions.value)) {
			if (!Array.isArray(filterIds)) continue

			const filterOptionForReset = filterOptions.value.find(option => option.categoryLabel === categoryLabel)?.filterValues

			if (!filterOptionForReset) continue

			for (const filterId of filterIds) {
				const filterValue = findFilterValueById(filterOptionForReset, filterId as CASelectInputType)

				if (!filterValue) continue

				removeFilterOption(categoryLabel, filterValue.label)
			}
		}

		pagination.value.currentPage = 1
		pagination.value.currentSearchPhrase = ''
		pagination.value.filterValues = {}
		pagination.value.perPage = PER_PAGE.COURSES
		paginationTotal.value = {
			complianceBundles: 0,
			fullCatalog: 0,
			specializedBundles: 0,
		}
		selectedFilterOptions.value = {}

		await fetchCatalogs()
	}

	return {
		// State
		careSettings,
		catalogList,
		catalogRouteName,
		complianceBundles,
		filterOptions,
		itemType,
		loading,
		pagination,
		paginationTotal,
		payorSource,
		roles,
		selectedFilterOptions,
		specializedBundles,

		// Getters
		filterParameters,
		selectedFilterCategories,
		showFilters,

		// Actions
		fetchCatalogs,
		fetchComplianceBundles,
		fetchFullCatalog,
		fetchSpecializedBundles,
		findFilterValueById,
		handleFilter,
		handlePageChange,
		handleRemoveFilter,
		handleSearchChange,
		resetAllFilters,
	}
})

export default useCatalogStore
